import React, { FC, useEffect } from 'react'
import { Box, Flex, Heading, Link } from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { breakpoint, styled } from '@moonpig/launchpad-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import { DepartmentsInspirationList } from './Inspirations/DepartmentsInspirationList'
import { Reminder } from './Reminders/Reminder'
import { Reminders } from './Reminders/Reminders'
import type {
  InspirationItemType,
  InspirationType,
  RecentSearchItemType,
  RecentSearchType,
  ReminderItemType,
  SubmitItemType,
} from './types'
import { RecentSearches } from './RecentSearches/RecentSearches'
import { useLocaleText } from '../locale'
import { DepartmentType } from './departmentMap'

const StyledContainer = styled.div.attrs<{ isOpen: number | boolean }>(
  ({ isOpen }) => ({
    className: !isOpen ? 'hidden' : undefined,
  }),
)<{ isOpen: number | boolean }>`
  position: relative;
  top: ${spacingPx(12)};
  z-index: 3;
  overflow-y: auto;
  max-height: calc(100dvh - 48px);
  ${s({
    pb: 4,
    pt: 6,
    px: 3,
    bgcolor: 'colorBackground01',
  })};

  &.hidden {
    display: none;
  }

  ${breakpoint('md')} {
    max-height: 75vh;
    overflow-y: auto;
    border-radius: 0px 0px 12px 12px;

    &::-webkit-scrollbar {
      width: 12px;
    }

    &::-webkit-scrollbar-track {
      ${s({
        bgcolor: 'colorBlack00',
      })}
      border-radius: 8px;
    }

    &::-webkit-scrollbar-thumb {
      ${s({
        bgcolor: 'colorBlack20',
      })}
      border-radius: 85px;
    }
  }
`

const StyledHeader = styled(Heading)`
  ${s({
    fontSize: '12px',
    lineHeight: '24px',
    fontWeight: 'bold',
    color: 'colorTextLabel',
    pl: 5,
    mb: 0,
  })};
`

const StyledHeaderFlex = styled(Flex)`
  ${s({
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  })}
`

const StyledClearAllButton = styled(Link)`
  position: absolute;
  text-decoration: none;
  ${s({
    color: 'colorInteractionTextLink',
    p: 0,
    m: 0,
    right: '16px',
    typography: 'typeButtonLabel',
    fontSize: '12px',
  })}
`

type RecentSearchesWithHeaderProps = {
  recentSearches: RecentSearchType[]
  getItemProps: (props: {
    itemIndex: number
    item: RecentSearchItemType
  }) => Record<string, unknown>
  getMenuProps: () => Record<string, unknown>
  onRemoveItem: (item: RecentSearchType) => void
  onClearRecentSearches: () => void
}

const RecentSearchesWithHeader: FC<RecentSearchesWithHeaderProps> = ({
  recentSearches,
  onClearRecentSearches,
  ...recentSearchProps
}) => {
  const t = useLocaleText()

  return (
    <Box data-testid="lp-nav-focusable-recent-search" mb={6}>
      <StyledHeaderFlex>
        <StyledHeader level="h4">{t('search.recently_searched')}</StyledHeader>
        {recentSearches.length > 1 && (
          <StyledClearAllButton
            tabIndex={0}
            onClick={onClearRecentSearches}
            data-testid="lp-nav-search-recent-clear"
            aria-label={t('search.clear_all_recent_searches_label')}
          >
            {t('search.clear_recent_searches')}
          </StyledClearAllButton>
        )}
      </StyledHeaderFlex>
      <RecentSearches recentSearches={recentSearches} {...recentSearchProps} />
    </Box>
  )
}

type FocusableSearchBoxProps = {
  isOpen: boolean
  inspirations: InspirationType[]
  reminders?: Reminder[]
  recentSearches: RecentSearchType[]
  inputValue: string | null
  inspirationsHeaderText?: string
  getItemProps: (props: {
    itemIndex: number
    item: RecentSearchItemType | InspirationItemType | ReminderItemType
  }) => Record<string, unknown>
  getMenuProps: () => Record<string, unknown>
  onRemindersVisible?: () => void
  onRemoveRecentSearch: (item: RecentSearchType) => void
  onClearRecentSearches: () => void
  onSelectItem: (selectedValue: string | SubmitItemType) => void
  activeDepartment: DepartmentType
  setHightlightedIndex: (index: number) => void
}

export const FocusableSearchBox: FC<FocusableSearchBoxProps> = ({
  isOpen,
  inspirations,
  reminders = [],
  recentSearches,
  inputValue,
  getItemProps,
  getMenuProps,
  onRemindersVisible = () => {},
  onRemoveRecentSearch,
  onClearRecentSearches,
  onSelectItem,
  activeDepartment,
  setHightlightedIndex,
}) => {
  const t = useLocaleText()

  const activeDepartmentInspirations =
    activeDepartment.name === 'all'
      ? inspirations
      : inspirations.filter(x => x.department === activeDepartment.enum)

  const inspirationsVisible =
    isOpen &&
    activeDepartmentInspirations.length &&
    (inputValue === null || inputValue === '')

  const recentSearchesVisible =
    isOpen &&
    recentSearches.length &&
    (inputValue === null || inputValue === '')

  const remindersVisible =
    isOpen && reminders.length && (inputValue === null || inputValue === '')

  useEffect(() => {
    if (remindersVisible) {
      onRemindersVisible()
    }
  }, [onRemindersVisible, remindersVisible])

  return (
    <StyledContainer
      isOpen={inspirationsVisible || recentSearchesVisible || remindersVisible}
      onMouseLeave={() => {
        setHightlightedIndex(-1)
      }}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        const { target, key, nativeEvent } = e
        const targetElement = target as HTMLDivElement
        if (
          key === 'Enter' &&
          targetElement.tagName === 'LI' &&
          targetElement.textContent
        ) {
          nativeEvent.preventDefault()

          const { itemType, parentDepartment, searchTerm, value, reminderId } =
            targetElement.dataset

          if (itemType === 'inspiration' || itemType === 'recent') {
            const submitItem = {
              department: parentDepartment,
              itemType,
              searchTerm,
              value: searchTerm,
            }

            return onSelectItem(
              submitItem as InspirationItemType | RecentSearchItemType,
            )
          }

          if (itemType === 'reminder') {
            const submitItem = {
              itemType,
              id: reminderId,
              value,
            }

            return onSelectItem(submitItem as ReminderItemType)
          }
        }
      }}
    >
      {recentSearchesVisible ? (
        <RecentSearchesWithHeader
          recentSearches={recentSearches}
          getItemProps={getItemProps}
          getMenuProps={getMenuProps}
          onClearRecentSearches={onClearRecentSearches}
          onRemoveItem={onRemoveRecentSearch}
        />
      ) : null}
      {remindersVisible ? (
        <Box data-testid="lp-nav-focusable-searchbox-reminders" mb={6}>
          <StyledHeader level="h4">
            {t('search.upcoming_occasions')}
          </StyledHeader>
          <Reminders
            reminders={reminders}
            getItemProps={getItemProps}
            getMenuProps={getMenuProps}
          />
        </Box>
      ) : null}
      {inspirationsVisible ? (
        <>
          <StyledHeader level="h4">{t('search.trending')}</StyledHeader>
          <div data-testid="lp-nav-focusable-searchbox-inspirations">
            <DepartmentsInspirationList
              inspirations={activeDepartmentInspirations}
              getItemProps={getItemProps}
              getMenuProps={getMenuProps}
              startIndex={recentSearches.length}
            />
          </div>
        </>
      ) : null}
    </StyledContainer>
  )
}
